import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Modal, Pill, SearchInput, useDebouncedValue, EmptyState } from '@bcecloud/portal-ui';
import { linkEventBooking, searchBookingRequests, type BookingRequestSummary } from '@/lib/events';
import { errorMessage } from '@/lib/api';

export interface BookingLinkDialogProps {
  readonly eventId: string;
  /** The booking currently on the event, if any. */
  readonly current: BookingRequestSummary | null;
  readonly open: boolean;
  readonly onClose: () => void;
}

function shortDate(iso: string | null): string {
  if (!iso) return 'No date';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}

/**
 * Links an event to the portal booking it delivers. Search by the booking's
 * reference (NW-4821) or program, pick one; the portal then shows the
 * client one engagement instead of a request and an event side by side.
 *
 * @param props - The event, its current link, and open/close.
 * @returns The dialog.
 */
export function BookingLinkDialog({ eventId, current, open, onClose }: BookingLinkDialogProps): React.ReactElement {
  const queryClient = useQueryClient();
  const [term, setTerm] = useState('');
  const debounced = useDebouncedValue(term.trim(), 250);

  const results = useQuery({
    queryKey: ['booking-requests', debounced],
    queryFn: ({ signal }) => searchBookingRequests(debounced, signal),
    enabled: open && debounced.length > 0,
  });

  const link = useMutation({
    mutationFn: (bookingRequestId: string | null) => linkEventBooking(eventId, bookingRequestId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['event', eventId] });
      onClose();
    },
  });

  return (
    <Modal
      open={open}
      title={current ? 'Change the portal request' : 'Link a portal request'}
      subtitle="The booking a client made in their portal that this event delivers. Their My events shows one engagement once the two are linked."
      onClose={onClose}
      actions={
        <>
          {current ? (
            <Button variant="ghost" className="mr-auto" disabled={link.isPending} onClick={() => link.mutate(null)}>
              Unlink
            </Button>
          ) : null}
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <SearchInput
          label="Search portal bookings"
          placeholder="Reference (NW-4821) or program"
          value={term}
          onChange={(event) => setTerm(event.target.value)}
          autoFocus
        />
        {link.isError ? <p className="text-portal-tiny text-portal-danger">{errorMessage(link.error)}</p> : null}
        {debounced.length === 0 ? (
          <p className="text-portal-tiny text-portal-muted">Type a reference or a program name to search every client's bookings.</p>
        ) : results.isPending ? (
          <p className="text-portal-tiny text-portal-muted">Searching…</p>
        ) : (results.data ?? []).length === 0 ? (
          <EmptyState title="No bookings match" message="Try the client's name, the reference or the program." />
        ) : (
          <ul className="divide-y divide-portal-line rounded-portal-card border border-portal-line">
            {(results.data ?? []).map((booking) => (
              <li key={booking.id} className="flex flex-wrap items-center gap-3 px-3 py-2.5">
                <span className="min-w-0 flex-1">
                  <span className="flex flex-wrap items-center gap-2">
                    <b className="text-portal-small">{booking.reference}</b>
                    <Pill tone={booking.id === current?.id ? 'brand' : 'neutral'}>
                      {booking.id === current?.id ? 'Linked now' : booking.status.toLowerCase().replace(/_/g, ' ')}
                    </Pill>
                  </span>
                  <span className="block text-portal-micro text-portal-muted">
                    {[booking.programName, booking.organizationName, shortDate(booking.preferredDate)].filter(Boolean).join(' · ')}
                  </span>
                </span>
                <Button
                  size="compact"
                  variant={booking.id === current?.id ? 'secondary' : 'primary'}
                  disabled={link.isPending || booking.id === current?.id}
                  onClick={() => link.mutate(booking.id)}
                >
                  {booking.id === current?.id ? 'Linked' : 'Link'}
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Modal>
  );
}
